import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { FiSearch, FiRefreshCw, FiFileText, FiClock } from 'react-icons/fi';
import MomentumAIIcon from '../../assets/images/Momentum AI Icon-bk.png';
import { handleUnauthorized } from '../../utils/apiClient';
import type { ChatMessage } from './CarrierFinderPage';
import PreviewIcon from '../../assets/images/ViewDetailsIcon.svg';
import '../commissionscan/CommissionScan.css';
import '../policycomparison/PolicyComparison.css';
import '../../styles/QuoteProposal.css';
import '../../styles/Voice.css';
import '../../styles/CarrierFinder.css';
const getToken = (): string => localStorage.getItem('toolboxToken') || '';
const PAGE_SIZE = 10;

interface HistoryRecord {
  conversationId: string;
  question: string;
  createdDate: string;
}

interface Toast { id: number; type: 'success' | 'error' | 'info'; message: string; }

interface Props {
  onLoadConversation: (conversationId: string, chats: ChatMessage[]) => void;
}

const formatDate = (d: string) => {
  if (!d) return '—';
  try {
    const dt = new Date(d);
    return (
      dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
      ' ' +
      dt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    );
  } catch { return d; }
};

const CarrierFinderHistory: React.FC<Props> = ({ onLoadConversation }) => {
  const [rows, setRows]                     = useState<HistoryRecord[]>([]);
  const [loading, setLoading]               = useState(false);
  const [search, setSearch]                 = useState('');
  const [page, setPage]                     = useState(1);
  const [loadingConvId, setLoadingConvId]   = useState<string | null>(null);
  const [toasts, setToasts]                 = useState<Toast[]>([]);
  const toastCounter                        = useRef(0);

  const showToast = useCallback((type: Toast['type'], message: string) => {
    const id = ++toastCounter.current;
    setToasts(p => [...p, { id, type, message }]);
    setTimeout(() => setToasts(p => p.filter(t => t.id !== id)), 4000);
  }, []);

  const fetchHistory = useCallback(() => {
    setLoading(true);
    axios.get(`${(process.env.REACT_APP_PolicyAgent_Url || '')}/api/mycarrier-finder/getchathistory`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then(res => {
        const data: HistoryRecord[] = Array.isArray(res.data?.chatHistory) ? res.data.chatHistory : [];
        setRows(data);
        setLoading(false);
      })
      .catch(err => {
        setLoading(false);
        if (err.response?.status === 401) handleUnauthorized();
        else showToast('error', 'Failed to load history.');
      });
  }, [showToast]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const filtered = rows.filter(r => {
    if (!search.trim()) return true;
    return r.question?.toLowerCase().includes(search.toLowerCase());
  });

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const mapChats = (raw: any[]): ChatMessage[] =>
    raw.map(c => ({ chatId: String(c.chatId), question: c.chatQuestion ?? '', answer: c.response ?? '' }));

  const openConversation = async (convId: string) => {
    setLoadingConvId(convId);
    try {
      const res = await axios.get(`${(process.env.REACT_APP_PolicyAgent_Url || '')}/api/mycarrier-finder/getchatbyconversation`, {
        params: { conversationId: convId },
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const chats = mapChats(Array.isArray(res.data?.chats) ? res.data.chats : []);
      onLoadConversation(convId, chats);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status: number } };
      if (axiosErr.response?.status === 401) handleUnauthorized();
      else showToast('error', 'Failed to load conversation.');
    } finally {
      setLoadingConvId(null);
    }
  };

  return (
    <>
      <div className="qp-history">

        {/* ── Header ── */}
        <div className="qp-history__header">
          <h3 className="qp-history__title">Carrier Finder — Chat History</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div className="qp-history__search">
              <FiSearch size={13} />
              <input
                type="text"
                placeholder="Search conversations…"
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
              />
            </div>
            <button className="csh-refresh-btn" onClick={fetchHistory} disabled={loading}>
              <FiRefreshCw size={13} /> Refresh
            </button>
          </div>
        </div>

        {/* ── Body ── */}
        <div className="qp-history__body">
          {loading ? (
            <div className="qp-history-loader" style={{ minHeight: 280 }}>
              <div className="qp-history-loader__orb">
                <div className="qp-history-loader__ring qp-history-loader__ring--1" />
                <div className="qp-history-loader__ring qp-history-loader__ring--2" />
                <div className="qp-history-loader__core"><FiClock size={16} /></div>
              </div>
              <div className="qp-history-loader__text">
                <p className="qp-history-loader__title">Loading History</p>
                <p className="qp-history-loader__sub">
                  Fetching carrier finder conversations
                  <span className="qp-init-loader__dots"><span>.</span><span>.</span><span>.</span></span>
                </p>
              </div>
            </div>
          ) : paged.length === 0 ? (
            <div className="qp-empty-state">
              <FiFileText size={28} />
              <p>{search ? 'No conversations match your search.' : 'No conversations yet. Start a new chat to get started.'}</p>
            </div>
          ) : (
            <div className="qp-table-wrap glass-surface">
              <table className="qp-table" style={{ tableLayout: 'fixed', width: '100%' }}>
                <colgroup>
                  <col style={{ width: '60%' }} />
                  <col style={{ width: '30%' }} />
                  <col style={{ width: '10%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th className="qp-table__th">First Question</th>
                    <th className="qp-table__th" style={{ textAlign: 'center' }}>Date</th>
                    <th className="qp-table__th" style={{ textAlign: 'center' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map(r => (
                    <tr key={r.conversationId} className="qp-table__row">
                      <td
                        className="qp-table__td"
                        style={{
                          color: 'var(--accent-purple-light)',
                          fontWeight: 500,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={r.question}
                      >
                        <img
                          src={MomentumAIIcon}
                          alt=""
                          width={16}
                          height={16}
                          style={{ marginRight: 6, verticalAlign: 'middle', flexShrink: 0, objectFit: 'contain' }}
                        />
                        {r.question || '—'}
                      </td>
                      <td className="qp-table__td" style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                        {formatDate(r.createdDate)}
                      </td>
                      <td className="qp-table__td" style={{ textAlign: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {loadingConvId === r.conversationId
                            ? <span className="pc-spinner" style={{ width: 16, height: 16, display: 'inline-block' }} />
                            : (
                              <img
                                src={PreviewIcon}
                                alt="Open"
                                title="Open conversation"
                                height={20}
                                style={{ cursor: 'pointer', opacity: 0.8, transition: 'opacity 0.15s' }}
                                onClick={() => openConversation(r.conversationId)}
                              />
                            )
                          }
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ── Pagination ── */}
          {filtered.length > PAGE_SIZE && (
            <div className="qp-pagination">
              <span className="qp-pagination__info">
                {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
              </span>
              <div className="qp-pagination__controls">
                <button className="qp-pagination__btn" disabled={page === 1} onClick={() => setPage(1)} title="First">«</button>
                <button className="qp-pagination__btn" disabled={page === 1} onClick={() => setPage(p => p - 1)} title="Previous">‹</button>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  const p = Math.max(1, page - 2) + i;
                  if (p > totalPages) return null;
                  return (
                    <button
                      key={p}
                      className={`qp-pagination__btn${page === p ? ' qp-pagination__btn--active' : ''}`}
                      onClick={() => setPage(p)}
                    >{p}</button>
                  );
                })}
                <button className="qp-pagination__btn" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} title="Next">›</button>
                <button className="qp-pagination__btn" disabled={page >= totalPages} onClick={() => setPage(totalPages)} title="Last">»</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Toasts ── */}
      <div className="pc-toast">
        {toasts.map(t => (
          <div key={t.id} className={`pc-toast__item pc-toast__item--${t.type}`}>{t.message}</div>
        ))}
      </div>
    </>
  );
};

export default CarrierFinderHistory;

